import { useGetTemplatesQuery } from '../../../../apis/apiApplication';
import { getSelectedProjectShortName } from '../../../../apis/apiHeader';
import { skipToken } from '@reduxjs/toolkit/query';
import React, { useCallback, useMemo, useState } from 'react';
import { Row } from '@ydistri/ds';
import DeleteIconButton from '../../../../components/buttons/DeleteIconButton';
import { styled } from 'styled-components';
import { MdDragIndicator } from 'react-icons/md';
import { DragHandle } from '../../../Configuration/ConfigurationStoresAndDepartments/ExceptionDefinition';
import { Reorder, useDragControls } from 'framer-motion';
import Validator from '../../../Configuration/Validator/Validator';
import { createTemplateOrCalculationObject } from '../../../../hooks/useTemplateOrCalculation';
import { createTemplateOrCalculationWithValue } from '../../../../hooks/useTemplateOrCalculationWithValue';

const TemplateRowStyled = styled(Row)`
  width: 60rem;
  max-width: 60rem;
  flex-wrap: nowrap;
  margin-bottom: 0.5rem;
`;

const TemplateTitleWrapper = styled(Row)`
  width: 33.75rem;
`;

interface TemplateRowProps {
  validator: boolean;
  templateId: number;
  children?: React.ReactNode;
  onRemoveTemplate: (x: number) => void;
  /** The validator's verdict for this template; the modal gates its OK button on every row's. */
  onValidChange?: (templateId: number, valid: boolean) => void;
}

const TemplateRow: React.FC<TemplateRowProps> = ({
  validator,
  templateId,
  children,
  onRemoveTemplate,
  onValidChange,
}) => {
  const onValidChangeHandler = useCallback(
    (valid: boolean) => onValidChange?.(templateId, valid),
    [onValidChange, templateId],
  );

  const controls = useDragControls();
  const [grabbing, setGrabbing] = useState(false);

  const { data: templates } = useGetTemplatesQuery(getSelectedProjectShortName() ?? skipToken);
  const template = useMemo(
    () => (templates ?? []).find(template => template.id === templateId),
    [templates, templateId],
  );

  const deleteHandler = useCallback(
    () => onRemoveTemplate(templateId),
    [templateId, onRemoveTemplate],
  );

  const onDragStart = useCallback(() => {
    setGrabbing(true);
  }, []);

  const onDragEnd = useCallback(() => {
    setGrabbing(false);
  }, []);

  const onPointerDownHandler = useCallback(
    (e: PointerEvent | React.PointerEvent) => controls.start(e),
    [controls],
  );

  if (!template) return null;

  return (
    <Reorder.Item
      as="div"
      value={templateId}
      dragListener={false}
      dragControls={controls}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      <TemplateRowStyled $alignItems="center" $gap="1rem">
        <DragHandle $grabbing={grabbing} onPointerDown={onPointerDownHandler}>
          <MdDragIndicator size={20} />
        </DragHandle>
        <TemplateTitleWrapper $gap="0.5rem" $alignItems="center" $flexWrap="nowrap">
          {children}
        </TemplateTitleWrapper>
        <DeleteIconButton onClick={deleteHandler} tooltip="Remove template" iconType="x" />
        {validator && (
          <Validator
            key={templateId}
            templateOrCalculation={createTemplateOrCalculationWithValue(
              createTemplateOrCalculationObject('Template', templateId),
            )}
            onValidChange={onValidChangeHandler}
            size="small"
          />
        )}
      </TemplateRowStyled>
    </Reorder.Item>
  );
};

export default TemplateRow;
